import React, { useEffect, useState, useRef, Dispatch, SetStateAction, useMemo, useCallback } from 'react';
import styles from './NeedInfoV2.module.scss';
import dayjs from 'dayjs';
import Tooltip from '../_custom/Tooltip/Tooltip';
import infoIcon from '../../assets/info.svg';
import useFetchRequirements, { EntityRequirement } from './CustomHooks/useFetchRequirements';
import useDrawerControls from './CustomHooks/useDrawerControls';
import CheckBox from '../CheckBox/CheckBox';
import CircularSelect from '../MagicUpload/components/CircularSelect/CircularSelect';
import RadioButton from '../RadioButton/RadioButton';
import { Button } from '../_custom';
import ReviewEmailDrawer from './components/ReviewEmailDrawer';
import AddClarificationDrawer from './components/AddClarificationDrawer';
import { useParams, useSearchParams } from 'react-router-dom';
import { SOURCE, AicaChaseStatus } from './NeedInfoV2Constants';
import { convertEpochToDate, getPlatformUserId, getSubdomain, isEmpty, isPnoUser, returnDaysAgo } from '../../utils/utils';
import { setShowInviteUserWithCallback } from '../../store/investor/action';
import {
    archive,
    copyIcon,
    emptyRequirements,
    greyCalendar,
    markEmailRead,
    plusPurpleCircle,
    purpleCalendar,
} from '../../assets/hostedassets';
import { useDispatch } from 'react-redux';
import { fetchInternalSummonAicaData, InternalSummonAicaDataPayload, InternalSummonUserType } from '../../store/user/actions';
import { toast } from 'react-toastify';
import { useAppSelector } from '../../app/hooks';
import { Mixpanel } from '../../utils/mixpanel';
import useCopyText from '../_custom/Hooks/useCopyText/useCopyText';
import ThreeDotsIcon from '../../assets/Svg/ThreeDotsIcon';
import ForwardToInboxIcon from '../../assets/Svg/ForwardToInboxIcon';
import { HEADER_TITLE, REQ_SOURCE, SHOW_AI_FOR } from '../MagicUpload/Utils/MagicUploadConstants';
import { isNil } from 'lodash';
import { useOutsideClick } from '../../utils/constants/hooks';
import ConfigurableModal from '../ConfigurableModal/ConfigurableModal';
import { USER_TYPE } from '../../utils/constants/user';
import AIText from '../AIText/AIText';
import MagicUploadCenter from '../MagicUpload/components/MagicUploadCenter/MagicUploadCenter';
import { MagicUploadFileData } from '../MagicUpload/useFetchDataForMU';
import CommentsBox from './components/CommentsCell/CommentsCell';
import { AICA_DATA_PENDING_CLARIFICATION, AICA_DATA_PENDING_EDIT } from '../../utils/constants/mixpanelEvents/investorEvents';
import NeedInfoSkeleton from './components/NeedInfoSkeleton/NeedInfoSkeleton';
import ToggleButton from '../ToggleButton/ToggleButton';
import FilteredHeader from '../MagicUpload/components/FilteredHeader/FilteredHeader';
import MarkEmailRead from '../../assets/Svg/MarkEmailRead';
import { useVisibilityConfig } from '../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../utils/constants/visibilityKeys';
import clsx from 'clsx';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import RedirectIcon from '../../assets/Svg/RedirectIcon';
import { TAB_STATE, TabState } from './NeedInfoV2Constants';

type NeedInfoV2Props = {
    setLoading: Dispatch<SetStateAction<boolean>>;
    toggleDrawer: () => void;
    tabState: TabState;
    fileData: MagicUploadFileData[];
    setFileData: Dispatch<SetStateAction<MagicUploadFileData[]>>;
    hasEditPermission?: boolean;
    onCountsUpdate?: (activeCount: number, archivedCount: number) => void;
};

const NeedInfoV2: React.FC<NeedInfoV2Props> = ({
    setLoading,
    toggleDrawer,
    tabState,
    fileData,
    setFileData,
    hasEditPermission = true,
    onCountsUpdate,
}) => {
    const {
        lenderRequirements,
        setLenderRequirements,
        formatDate,
        updateRequirementOptimistically,
        handleAddItemSubmit,
        isAddItemDrawerOpen,
        setIsAddItemDrawerOpen,
        documentList,
        lastUpdatedAt,
        lastUpdatedBy,
        setLastUpdatedAt,
        docFetching,
        isAicaChaseEnabled,
        aicaChaseData,
        handleAicaChaseStatusToggle,
        lenderName,
        lenderId,
        dataTypes,
        requestedBy,
        isAddingClarifications,
        isRefreshingRequirements,
        isRefreshRequirementsModalOpen,
        handleOpenRefreshRequirementsModal,
        handleRefreshRequirements,
        isAddingBaseConfigData,
        handleAddBaseConfigData,
        handleUpdateDurationModalClose,
        handleOpenPortfolioPage,
        activeCount,
        archivedCount,
        requirementViewType,
        handleArchiveBatch,
        isArchivingBatch,
        // Compose email helpers
        getComposeEmailButtonText,
        isComposeEmailDisabled,
        getOrgNameForEmail,
        handleComposeEmailClick,
        isLenderRequest,
    } = useFetchRequirements(tabState); // Pass the dedicated requirement state
    const {
        closeDrawer: closeMainDrawer,
        isDrawerOpen: isMainDrawerOpen,
        sendNotification,
        userList,
        loading,
        getOrgUserList,
        formatDataGapList,
        fetchNeedInfoList,
        combinedOrgUserList,
        getCombinedOrgUserList,
    } = useDrawerControls(setLastUpdatedAt);
    const { isVisible } = useVisibilityConfig();
    const [searchParams] = useSearchParams();
    const [selectedRequirements, setSelectedRequirements] = useState<Set<string>>(new Set());
    const [isReviewEmailDrawerOpen, setIsReviewEmailDrawerOpen] = useState(false);
    const effectRanRef = useRef(false);
    const dispatch = useDispatch();
    const params = useParams();
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const [editableEntity, setEditableEntity] = useState<EntityRequirement | null>(null);
    const user = useAppSelector((state) => state.user);
    const contentRef = useRef<HTMLDivElement>(null);
    const tableHeaderRef = useRef<HTMLTableSectionElement>(null);
    const [skeletonRowCount, setSkeletonRowCount] = useState(5);
    const containerRef = useRef<HTMLDivElement>(null);
    const [openMenuDocId, setOpenMenuDocId] = useState<string | number | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState<string | number | null>(null);
    const [showArchiveModal, setShowArchiveModal] = useState<string | number | null>(null);
    const [showBulkArchiveModal, setShowBulkArchiveModal] = useState(false);
    const accountType = useAppSelector((state) => state.user.userData.accountType);
    const showMu = isVisible(VISIBILITY_KEYS.MAGIC_UPLOAD.ENABLED);
    const [isSelectionFooterVisible, setIsSelectionFooterVisible] = useState(false);
    const hasPreSelectedBaseData = useRef(false);

    // Filter state similar to MagicUpload
    const [filters, setFilters] = useState<{
        dataTypes: { value: string; label: string }[];
        requestedBy: { value: string; label: string }[];
    }>({
        dataTypes: [],
        requestedBy: [],
    });

    // Store original requirements to restore when filters are cleared
    const [originalRequirements, setOriginalRequirements] = useState<EntityRequirement[]>([]);

    // For primary footer (Add Clarification + AICA Chase only)
    const shouldShowPrimaryFooter = () => {
        return (
            tabState === TAB_STATE.ACTIVE &&
            (isVisible(VISIBILITY_KEYS.DATA_PENDING.ADD_CLARIFICATION) || isVisible(VISIBILITY_KEYS.DATA_PENDING.AICA_CHASE))
        );
    };

    // For selection footer (appears when items selected)
    const shouldShowSelectionFooter = () => {
        return (
            tabState === TAB_STATE.ACTIVE &&
            selectedRequirements.size > 0 &&
            (isVisible(VISIBILITY_KEYS.DATA_PENDING.USER_LIST) || isVisible(VISIBILITY_KEYS.DATA_PENDING.COMPOSE_EMAIL))
        );
    };

    // Store original requirements when they first load
    useEffect(() => {
        if (lenderRequirements.length > 0 && originalRequirements.length === 0) {
            setOriginalRequirements([...lenderRequirements]);
        }
    }, [lenderRequirements, originalRequirements.length]);

    // Apply filters directly to lenderRequirements
    useEffect(() => {
        if (originalRequirements.length === 0) return;

        const hasFilters = filters.dataTypes.length > 0 || filters.requestedBy.length > 0;

        if (hasFilters) {
            let filtered = [...originalRequirements];

            // Filter by Data Type
            if (filters.dataTypes.length > 0) {
                const selectedDataTypes = filters.dataTypes.map((filter) => filter.value);
                filtered = filtered.filter((req) => selectedDataTypes.includes(req.dataType));
            }

            // Filter by Requested By
            if (filters.requestedBy.length > 0) {
                const selectedRequestedBy = filters.requestedBy.map((filter) => filter.value);
                filtered = filtered.filter(
                    (req) => req.requestedBy && req.requestedBy.some((requester) => selectedRequestedBy.includes(requester)),
                );
            }

            setLenderRequirements(filtered);

            // Update selectedRequirements to only include IDs that are still visible after filtering
            const filteredIds = new Set(filtered.map((req) => String(req.id)));
            setSelectedRequirements((prev) => {
                const newSelected = new Set<string>();
                prev.forEach((id) => {
                    if (filteredIds.has(id)) {
                        newSelected.add(id);
                    }
                });
                return newSelected;
            });
        } else {
            setLenderRequirements([...originalRequirements]);
        }
    }, [filters, originalRequirements, setLenderRequirements]);

    // Memoize the filter dropdown items to prevent infinite re-renders
    const dataTypeOptions = useMemo(() => {
        return dataTypes.map((dataType) => ({ value: dataType, label: dataType }));
    }, [dataTypes]);

    const requestedByOptions = useMemo(() => {
        return requestedBy.map((requester) => ({ value: requester, label: requester }));
    }, [requestedBy]);

    // Filter handler similar to MagicUpload
    const handleFilterOptions = (items: { value: string; label: string }[], key: string) => {
        setFilters((prev) => {
            const updatedFilters = { ...prev, [key]: items };
            if (JSON.stringify(prev) !== JSON.stringify(updatedFilters)) {
                return updatedFilters;
            }
            return prev;
        });
    };

    // Handle outside click to close menu
    useEffect(() => {
        if (openMenuDocId === null) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpenMenuDocId(null);
            }
        };

        // Add a small delay to prevent immediate closing when opening
        const timeoutId = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
        }, 0);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openMenuDocId]);

    const returnDay = (lastUpdatedAt: string) => {
        let daysAgo = '';
        try {
            daysAgo = returnDaysAgo(dayjs(lastUpdatedAt).format('YYYY-MM-DD'));
        } catch (e) {
            daysAgo = '';
        }
        return daysAgo;
    };

    useEffect(() => {
        if (docFetching && contentRef.current && tableHeaderRef.current) {
            const containerHeight = contentRef.current.offsetHeight;
            const headerHeight = tableHeaderRef.current.offsetHeight;
            const rowHeight = 56; // Estimated row height in pixels
            const availableHeight = containerHeight - headerHeight;
            const calculatedRowCount = Math.max(1, Math.floor(availableHeight / rowHeight));
            setSkeletonRowCount(calculatedRowCount);
        }
    }, [docFetching]);

    // useEffect(() => {  // stopping default selection of all requirements
    //     if (!isEmpty(lenderRequirements) && !effectRanRef.current) {
    //         const allIds = new Set(lenderRequirements.map((req) => String(req.id)));
    //         setSelectedRequirements(allIds);
    //         effectRanRef.current = true;
    //     }
    // }, [lenderRequirements]);

    // Pre-select Base Data entries on initial load (only on Active tab)
    useEffect(() => {
        if (!isEmpty(lenderRequirements) && !hasPreSelectedBaseData.current && !docFetching && tabState === TAB_STATE.ACTIVE) {
            // Filter requirements with source as LENDER_BASE_CONFIG or PNO_BASE_CONFIG
            const baseDataIds = lenderRequirements.filter((req) => REQ_SOURCE.includes(req.source)).map((req) => String(req.id));

            if (baseDataIds.length > 0) {
                setSelectedRequirements(new Set(baseDataIds));
                hasPreSelectedBaseData.current = true;
            }
        }
    }, [lenderRequirements, docFetching, tabState]);

    useEffect(() => {
        if (user.userData.accountType === USER_TYPE.COMPANY) {
            handleAddBaseConfigData();
        }
    }, [user.userData.accountType]);

    // Notify parent of count updates
    useEffect(() => {
        if (onCountsUpdate) {
            onCountsUpdate(activeCount, archivedCount);
        }
    }, [activeCount, archivedCount, onCountsUpdate]);

    // Manage selection footer animation
    useEffect(() => {
        if (selectedRequirements.size > 0 && tabState === TAB_STATE.ACTIVE) {
            setIsSelectionFooterVisible(true);
        } else {
            setIsSelectionFooterVisible(false);
        }
    }, [selectedRequirements.size, tabState]);

    const generateGSTLink = (): Promise<string> => {
        return new Promise((resolve, reject) => {
            const platformUserId = getPlatformUserId();
            const payload: InternalSummonAicaDataPayload = {
                parentOrgId: platformData?.investorOrgId as string,
                parentEmailId: user.userData.investorPrimaryEmail,
                orgId: params.id ?? (searchParams.get('orgId') as string),
                userType: InternalSummonUserType.ASSESSEE,
                requesterUserId: platformUserId,
                isMagicLink: true,
            };

            dispatch(
                fetchInternalSummonAicaData(payload, {
                    onSuccess: (userData, link) => {
                        resolve(`https://${link}`);
                    },
                    onError: (error) => {
                        reject(new Error('Failed to fetch the link'));
                    },
                }),
            );
        });
    };

    const openGSTLink = async () => {
        try {
            const link = await generateGSTLink();
            window.open(link, '_blank');
        } catch (error) {
            console.error('Error opening GST link:', error);
            toast.error('Failed to open GST link. Please try again.');
        }
    };

    const insertLinkIntoText = (text: string, link: string | undefined) => {
        // this function will return a jsx element with the link inserted into the text where ${LINK} is found
        if (!link) return text;
        const splitText = text.split('${LINK}');
        if (link === 'MAGIC_LINK_GST_R1') {
            return (
                <div className={styles.MagicLink}>
                    <span>
                        {splitText[0]}
                        <span
                            className={styles.DynamicLink}
                            onClick={(e) => {
                                e.stopPropagation();
                                openGSTLink();
                            }}
                        >
                            Link
                        </span>
                        {splitText[1]}
                    </span>
                    <span
                        onClick={async (e) => {
                            e.stopPropagation();
                            await copyGenerateGSTLink();
                            Mixpanel.track('GST Fetch Link Copied', {
                                'user Role': user?.userData?.accountType,
                                'PNO Platform': getSubdomain(),
                                'User Name': user?.userData?.userName,
                                'Current URL': window.location.href,
                                'Organization ID': params.id ?? user?.userData?.assesseeOrgId,
                            });
                        }}
                    >
                        <img src={copyIcon} alt="copyText" height={12} />
                    </span>
                </div>
            );
        }
        return (
            <>
                {splitText[0]}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <a className={styles.DynamicLink} href={link} target={'_blank'} rel="noreferrer">
                        Link
                    </a>
                    <img
                        src={copyIcon}
                        alt="Copy"
                        style={{ cursor: 'pointer', width: '12px', height: '12px' }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigator.clipboard.writeText(link);
                            toast.success('Text copied to clipboard');
                        }}
                    />
                </span>
                {splitText[1]}
            </>
        );
    };

    /**
     * @description Renders the combined org user list of company and partner users on tooltip
     * @returns JSX.Element
     */
    const renderCombinedOrgUserList = () => {
        return (
            <>
                {/* Company Users Section */}
                {combinedOrgUserList?.assesseeUsers && combinedOrgUserList?.assesseeUsers?.length > 0 && (
                    <>
                        <div className={styles.UserListHeader}>Company Users:</div>
                        {combinedOrgUserList.assesseeUsers.map((user) => (
                            <div key={user.userId} style={{ marginLeft: '16px', marginBottom: '4px' }}>
                                <span className={styles.UserListName}>{user.name || ''}</span> (
                                {user.emailVerified ? (
                                    user.emailId || ''
                                ) : isEmpty(user.emailId) ? (
                                    <span style={{ color: '#cbcbcb' }}>Email not available</span>
                                ) : (
                                    <span style={{ color: '#cbcbcb' }}>Email not verified</span>
                                )}{' '}
                                | {user.mobileNumber || '-'})
                            </div>
                        ))}
                    </>
                )}

                {/* Partner Users Section */}
                {combinedOrgUserList?.partnerUsers && combinedOrgUserList?.partnerUsers?.length > 0 && (
                    <>
                        <div className={styles.UserListHeader}>Partner Users:</div>
                        {combinedOrgUserList.partnerUsers.map((user) => (
                            <div key={user.userId} style={{ marginLeft: '16px', marginBottom: '4px' }}>
                                <span className={styles.UserListName}>{user.name || ''}</span> (
                                {user.emailVerified ? (
                                    user.emailId || ''
                                ) : isEmpty(user.emailId) ? (
                                    <span style={{ color: '#cbcbcb' }}>Email not available</span>
                                ) : (
                                    <span style={{ color: '#cbcbcb' }}>Email not verified</span>
                                )}{' '}
                                | {user.mobileNumber || ''})
                            </div>
                        ))}
                    </>
                )}
            </>
        );
    };

    const tooltipContent = () => {
        return (
            <>
                {renderCombinedOrgUserList()}
                <div
                    onClick={() =>
                        dispatch(
                            setShowInviteUserWithCallback({
                                show: true,
                                onSuccess: () => {
                                    getCombinedOrgUserList();
                                },
                            }),
                        )
                    }
                    className={styles.AddCompanyUser}
                >
                    <img src={plusPurpleCircle} alt="Add Company User" />
                    Add Company User
                </div>
            </>
        );
    };

    const { copy: copyGenerateGSTLink } = useCopyText(generateGSTLink);
    const { copy: copyDataGapList } = useCopyText(fetchNeedInfoList, formatDataGapList, lenderRequirements);

    // Get the selected requirement objects based on the IDs in the selectedRequirements set
    const selectedRequirementObjects = lenderRequirements.filter((req) => selectedRequirements.has(String(req.id)));

    const handleCheckboxChange = (requirementId: string, checked: boolean, type: 'single' | 'all') => {
        setSelectedRequirements((prev) => {
            const newSet = new Set(prev);
            if (type === 'single') {
                if (checked) {
                    newSet.add(requirementId);
                } else {
                    newSet.delete(requirementId);
                }
            } else if (type === 'all') {
                if (checked) {
                    const allIds = new Set(lenderRequirements.map((req) => String(req.id)));
                    return allIds;
                } else {
                    return new Set<string>();
                }
            }
            return newSet;
        });
    };

    // Write isAllSelected to check if all requirements are selected where status is not 'COMPLETE'
    const isAllSelected =
        lenderRequirements.length > 0 &&
        lenderRequirements.every((req) => selectedRequirements.has(String(req.id)) || req.status === 'COMPLETE');

    const handleRequirementUpdate = async (requirement: EntityRequirement) => {
        // Use the centralized update function
        await updateRequirementOptimistically(String(requirement.id), {
            status: requirement.status,
            markedAt: requirement.markedAt,
        });
        setLastUpdatedAt(dayjs().toString());
    };

    const handleDataTypeSelect = async (value: string, requirement: EntityRequirement) => {
        // Call the hook's update function directly
        await updateRequirementOptimistically(String(requirement.id), { dataType: value });
        setLastUpdatedAt(dayjs().toString());
        return Promise.resolve(true);
    };

    const handleOpenReviewEmailDrawer = () => {
        if (selectedRequirements.size === 0) {
            return;
        }
        setIsReviewEmailDrawerOpen(true);
    };

    const handleSubmitReviewEmail = async (clarification: string[], requirementIds: string[]) => {
        // TODO: Implement API call
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setLastUpdatedAt(dayjs().toString());
        setSelectedRequirements(new Set());
        setIsReviewEmailDrawerOpen(false);
    };

    const handleArchiveSelected = async () => {
        if (selectedRequirements.size === 0) return;
        setSelectedRequirements(new Set());

        // Use batch archive function
        await handleArchiveBatch(Array.from(selectedRequirements));
    };

    // Handlers for the Add Clarification Item Drawer
    const handleOpenAddItemDrawer = () => {
        setIsAddItemDrawerOpen(true);

        Mixpanel.track(AICA_DATA_PENDING_CLARIFICATION, {
            userRole: user?.userData?.accountType,
            pnoPlatform: getSubdomain(),
            userId: user?.userData?.userId,
            currentUrl: window.location.href,
            organizationId: params.id ?? user?.userData?.assesseeOrgId,
        });
    };

    const handleCloseAddItemDrawer = () => {
        setIsAddItemDrawerOpen(false);
        setEditableEntity(null);
    };

    const handleAicaChaseToggle = async () => {
        if (aicaChaseData?.status === AicaChaseStatus.NOT_APPLICABLE) return;

        // Determine the new status based on current status
        const newStatus = aicaChaseData?.status === AicaChaseStatus.ACTIVE ? AicaChaseStatus.TURNED_OFF : AicaChaseStatus.ACTIVE;

        // Call the API function from the hook
        const success = await handleAicaChaseStatusToggle(newStatus);

        // Track the toggle action
        const isLenderRequest = searchParams.get(SOURCE.LENDER_REQ) === 'true';
        Mixpanel.track('AICA Chase Toggle', {
            'user Role': user?.userData?.accountType,
            'PNO Platform': getSubdomain(),
            'User Name': user?.userData?.userName,
            'Current URL': window.location.href,
            'Organization ID': params.id ?? user?.userData?.assesseeOrgId,
            'Deal ID': isLenderRequest ? searchParams.get('dealId') : null,
            'Request Type': isLenderRequest ? 'Lender' : 'Borrower',
            'AICA Chase Enabled': newStatus === AicaChaseStatus.ACTIVE,
            'Toggle Success': success,
        });
    };

    const isRequirementRefreshing = (requirementId: any) => {
        return isRefreshingRequirements && selectedRequirements.has(String(requirementId));
    };

    // Function to render AICA chase status with appropriate styling
    const renderAicaChaseStatus = (status: AicaChaseStatus, epochTime?: number | null): JSX.Element => {
        switch (status) {
            case AicaChaseStatus.ACTIVE:
                if (epochTime) {
                    const formattedDate = dayjs(epochTime).format('h:mm A, DD MMM YYYY');
                    return <span className={`${styles.AicaChaseTimestamp}`}>{formattedDate}</span>;
                }
                return <span></span>;
            case AicaChaseStatus.ATTEMPTS_OVER:
                return <span style={{ color: '#000000', background: '#ffffff', fontSize: '0.875rem' }}>Attempts Over</span>;

            case AicaChaseStatus.TURNED_OFF:
                return <span style={{ color: '#000000', background: '#ffffff', fontSize: '0.875rem' }}>Turned Off</span>;

            case AicaChaseStatus.NOT_APPLICABLE:
                return <span style={{ color: '#CBCBCB', background: '#ffffff', fontSize: '0.875rem' }}>Not Applicable</span>;

            default:
                return <span></span>;
        }
    };

    const renderRow = (requirement: EntityRequirement, className: string = styles.PrimaryMessage): JSX.Element => {
        return (
            <span className={className}>
                {requirement.primaryMessage}
                {requirement.secondaryMessage && ': '}
                {requirement.secondaryMessage && insertLinkIntoText(requirement.secondaryMessage, requirement.link ?? undefined)}
            </span>
        );
    };

    return (
        <>
            <div
                ref={contentRef}
                style={!isVisible(VISIBILITY_KEYS.DATA_PENDING.SELECT_LINE_ITEM) ? { height: '90%' } : {}}
                className={styles.Content}
            >
                {showMu && searchParams.get('hideMu') !== 'true' && !docFetching && (
                    <MagicUploadCenter
                        setLoading={setLoading}
                        fileData={fileData}
                        setFileData={setFileData}
                        tabState={tabState}
                        orientation={'vertical'}
                    />
                )}

                {lenderRequirements.length === 0 && !docFetching ? (
                    <div className={styles.EmptyState}>
                        {!isAddingBaseConfigData ? (
                            <>
                                <img src={emptyRequirements} alt="Empty" />
                                {searchParams.get(SOURCE.LENDER_REQ) === 'true' ? (
                                    <div>
                                        {requirementViewType === TAB_STATE.ACTIVE ? 'No active data requests' : 'No archived data requests'}
                                    </div>
                                ) : (
                                    <>
                                        <div>
                                            {requirementViewType === TAB_STATE.ACTIVE
                                                ? 'No active data requests. Click below to generate data requests'
                                                : 'No archived data requests'}
                                        </div>
                                        {requirementViewType === TAB_STATE.ACTIVE && (
                                            <div>
                                                <Button
                                                    text="Generate Data Requests"
                                                    iconSize="12px"
                                                    onClick={() => {
                                                        handleAddBaseConfigData();
                                                    }}
                                                    variant="primary"
                                                    style={{ margin: 0 }}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        ) : (
                            <LoadingSpinner color="#6021B3" height={'40px'} />
                        )}
                    </div>
                ) : (
                    <table className={styles.Table}>
                        <thead ref={tableHeaderRef} className={styles.TableHeader}>
                            <tr>
                                {isVisible(VISIBILITY_KEYS.DATA_PENDING.SELECT_LINE_ITEM) && (
                                    <th className={styles.TableHeaderCell} style={{ width: '3%' }}>
                                        <CheckBox
                                            label=""
                                            checked={isAllSelected}
                                            onCheck={(checked) => handleCheckboxChange('', checked, 'all')}
                                            variant="primary"
                                            needPadding={false}
                                            disabled={isRefreshingRequirements || tabState === TAB_STATE.ARCHIVED}
                                        />
                                    </th>
                                )}
                                <th
                                    style={
                                        !isVisible(VISIBILITY_KEYS.DATA_PENDING.SELECT_LINE_ITEM)
                                            ? { padding: '0 1rem', width: '40%' }
                                            : { paddingLeft: '0', width: '40%', paddingRight: '0.5rem' }
                                    }
                                    className={styles.TableHeaderCell}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <span>
                                            {searchParams.get(SOURCE.BORROWER_REQ) === 'true'
                                                ? 'Pending data & clarification'
                                                : 'Pending Queries'}
                                        </span>
                                        {searchParams.get(SOURCE.BORROWER_REQ) && (
                                            <span
                                                className={styles.copyButton}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    copyDataGapList();
                                                    Mixpanel.track('Pending Data List Copied', {
                                                        'user Role': user?.userData?.accountType,
                                                        'PNO Platform': getSubdomain(),
                                                        'User Name': user?.userData?.userName,
                                                        'Current URL': window.location.href,
                                                        'Organization ID': params.id ?? user?.userData?.assesseeOrgId,
                                                    });
                                                }}
                                            >
                                                <img src={copyIcon} alt="copyText" />
                                                <span>Copy Pending List</span>
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th className={styles.TableHeaderCell} style={{ paddingRight: '0.5rem', width: '15%' }}>
                                    Requested
                                </th>
                                <th className={styles.TableHeaderCell} style={{ paddingLeft: '8px', width: '15%', paddingRight: '0.5rem' }}>
                                    Remarks
                                </th>
                                <FilteredHeader
                                    onFilter={handleFilterOptions}
                                    title={'Data Type'}
                                    items={dataTypeOptions}
                                    disabled={false}
                                    defaultSelectAll={false}
                                    style={{
                                        background: '#f9fafc',
                                        padding: '0.5rem 0 0.5rem 0.5rem',
                                        textAlign: 'left',
                                        fontSize: '0.85rem',
                                        fontFamily: 'Gilroy-semibold, sans-serif',
                                        color: '#545b62',
                                        borderBottom: '1px solid #e0e0e0',
                                        width: '15%',
                                    }}
                                />
                                {searchParams.get(SOURCE.BORROWER_REQ) === 'true' && (
                                    <FilteredHeader
                                        onFilter={handleFilterOptions}
                                        title={HEADER_TITLE.REQUESTED_BY}
                                        items={requestedByOptions}
                                        disabled={false}
                                        defaultSelectAll={false}
                                        style={{
                                            background: '#f9fafc',
                                            padding: '0.5rem 0 0.5rem 0.5rem',
                                            textAlign: 'left',
                                            fontSize: '0.85rem',
                                            fontFamily: 'Gilroy-semibold, sans-serif',
                                            color: '#545b62',
                                            borderBottom: '1px solid #e0e0e0',
                                            width: '10%',
                                        }}
                                    />
                                )}
                                <th className={styles.TableHeaderCell} style={{ paddingRight: '6rem', width: '10%' }}>
                                    Status
                                </th>
                                {searchParams.get(SOURCE.BORROWER_REQ) === 'true' && (
                                    <th className={styles.TableHeaderCell} style={{ paddingRight: '3rem', width: '5%' }}></th>
                                )}
                                {searchParams.get(SOURCE.LENDER_REQ) === 'true' && (
                                    <th className={styles.TableHeaderCell} style={{ paddingRight: '3rem', width: '10%' }}>
                                        Responded
                                    </th>
                                )}
                            </tr>
                        </thead>
                        {docFetching ? (
                            <NeedInfoSkeleton count={skeletonRowCount} />
                        ) : (
                            <tbody className={styles.DataContent}>
                                {lenderRequirements.map((requirement) => (
                                    <tr
                                        key={requirement.id}
                                        className={clsx(styles.TableRow, {
                                            [styles.selected]: selectedRequirements.has(String(requirement.id)),
                                            [styles.disabled]: tabState === TAB_STATE.ARCHIVED,
                                        })}
                                    >
                                        {isVisible(VISIBILITY_KEYS.DATA_PENDING.SELECT_LINE_ITEM) && (
                                            <td style={{ width: '3%' }} className={styles.TableCell}>
                                                <CheckBox
                                                    label=""
                                                    disabled={isRefreshingRequirements || tabState === TAB_STATE.ARCHIVED}
                                                    checked={selectedRequirements.has(requirement.id as string)}
                                                    onCheck={(checked) => handleCheckboxChange(requirement.id as string, checked, 'single')}
                                                    variant="primary"
                                                    needPadding={false}
                                                />
                                            </td>
                                        )}
                                        <td
                                            style={
                                                !isVisible(VISIBILITY_KEYS.DATA_PENDING.SELECT_LINE_ITEM)
                                                    ? { width: '40%', padding: '0 1rem' }
                                                    : { width: '40%', paddingRight: '0.5rem' }
                                            }
                                            className={styles.TableCell}
                                        >
                                            <span className={styles.PrimaryMessage}>
                                                {selectedRequirements.has(String(requirement.id)) && isRefreshingRequirements ? (
                                                    <AIText
                                                        text={renderRow(requirement)}
                                                        animate={true}
                                                        style={{
                                                            background:
                                                                'linear-gradient(90deg, #FFFFFF -13.76%, #4D4D4D 26.14%, #4D4D4D 93.76%)',
                                                            backgroundSize: '200% 200%',
                                                            WebkitTextFillColor: 'transparent',
                                                            WebkitBackgroundClip: 'text',
                                                        }}
                                                    />
                                                ) : SHOW_AI_FOR.includes(requirement.source) ? (
                                                    <AIText text={renderRow(requirement)} animate={false} />
                                                ) : (
                                                    renderRow(requirement)
                                                )}
                                            </span>
                                        </td>
                                        <td style={{ borderBottom: '1px solid #e0e0e0' }}>
                                            <div
                                                className={styles.RequestedColumn}
                                                style={{ width: searchParams.get(SOURCE.LENDER_REQ) === 'true' ? '40%' : '60%' }}
                                            >
                                                {!isEmpty(requirement.companyRequirementAudit) ? (
                                                    <div className={styles.Requestedtext}>
                                                        <img
                                                            src={markEmailRead}
                                                            alt="requested"
                                                            width={16}
                                                            height={16}
                                                            style={{
                                                                opacity:
                                                                    isRefreshingRequirements &&
                                                                    selectedRequirements.has(String(requirement.id))
                                                                        ? 0.5
                                                                        : 1,
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className={styles.Requestedtext}>
                                                        <span
                                                            style={{ color: isRequirementRefreshing(requirement.id) ? '#9D9D9D' : '#000' }}
                                                        >
                                                            -
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ width: '15%', paddingRight: '0.5rem' }} className={styles.TableCell}>
                                            <CommentsBox
                                                text={requirement.remark || ''}
                                                handleChange={async (text) => {
                                                    await updateRequirementOptimistically(String(requirement.id), {
                                                        remark: text,
                                                        remarkUpdatedBy: user.userData.userId,
                                                    });
                                                }}
                                                disabled={isRequirementRefreshing(requirement.id) || tabState === TAB_STATE.ARCHIVED}
                                                timestamp={
                                                    requirement.remarkUpdatedAt
                                                        ? dayjs(requirement.remarkUpdatedAt).format('DD MMM YYYY, h:mm A')
                                                        : ''
                                                }
                                            />
                                        </td>
                                        <td style={{ width: '15%', paddingRight: '0.5rem' }} className={styles.TableCell}>
                                            <CircularSelect
                                                inputStyles={
                                                    isRequirementRefreshing(requirement.id) || tabState === TAB_STATE.ARCHIVED
                                                        ? { backgroundColor: '#EBEBEB', color: '#9d9d9d', fontSize: '0.75rem' }
                                                        : { fontSize: '0.75rem' }
                                                }
                                                option={documentList}
                                                onSelect={(value) => handleDataTypeSelect(value, requirement)}
                                                selectedOption={requirement.dataType}
                                                placeholder="Select Data Type"
                                                disabled={
                                                    !isVisible(VISIBILITY_KEYS.DATA_PENDING.SELECT_LINE_ITEM) ||
                                                    isRequirementRefreshing(requirement.id) ||
                                                    tabState === TAB_STATE.ARCHIVED
                                                }
                                            />
                                        </td>
                                        {searchParams.get(SOURCE.BORROWER_REQ) === 'true' && (
                                            <td style={{ width: '10%', padding: '0.5rem' }} className={styles.TableCell}>
                                                <div className={styles.RequestedBy}>
                                                    {requirement.requestedBy &&
                                                    requirement.requestedBy.length > 1 &&
                                                    !isRequirementRefreshing(requirement.id) ? (
                                                        <Tooltip
                                                            containerFullWidth={true}
                                                            content={
                                                                <ol style={{ margin: 0, paddingLeft: '20px' }}>
                                                                    {requirement.requestedBy?.map((requester, index) => (
                                                                        <li key={index}>{requester}</li>
                                                                    ))}
                                                                </ol>
                                                            }
                                                            direction="left"
                                                        >
                                                            <div className={styles.RequestedByText}>
                                                                {requirement.requestedBy?.[0]}
                                                                {requirement.requestedBy && requirement.requestedBy.length > 1 && (
                                                                    <span>+{requirement.requestedBy.length - 1} more</span>
                                                                )}
                                                            </div>
                                                        </Tooltip>
                                                    ) : (
                                                        <div
                                                            className={clsx(styles.RequestedByText, {
                                                                [styles.disabled]: isRequirementRefreshing(requirement.id),
                                                            })}
                                                        >
                                                            {requirement.requestedBy?.[0]}
                                                            {requirement.requestedBy && requirement.requestedBy.length > 1 && (
                                                                <span>+{requirement.requestedBy.length - 1} more</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                        <td style={{ width: '10%' }} className={styles.TableCell}>
                                            <div className={styles.StatusCell}>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: '0.25rem',
                                                    }}
                                                >
                                                    <RadioButton
                                                        checked={requirement.status === 'COMPLETE'}
                                                        disabled={
                                                            searchParams.get(SOURCE.LENDER_REQ) === 'true' ||
                                                            !isVisible(VISIBILITY_KEYS.DATA_PENDING.MARK_AS_COMPLETED) ||
                                                            isRequirementRefreshing(requirement.id) ||
                                                            tabState === TAB_STATE.ARCHIVED
                                                        }
                                                        onChange={async (checked) => {
                                                            if (
                                                                searchParams.get(SOURCE.LENDER_REQ) === 'true' ||
                                                                !isVisible(VISIBILITY_KEYS.DATA_PENDING.MARK_AS_COMPLETED) ||
                                                                isRequirementRefreshing(requirement.id) ||
                                                                tabState === TAB_STATE.ARCHIVED
                                                            )
                                                                return;
                                                            await handleRequirementUpdate({
                                                                ...requirement,
                                                                status: checked ? 'COMPLETE' : 'PENDING',
                                                                markedAt: checked ? dayjs().toString() : null,
                                                            });
                                                            // setSelectedRequirements((prev) => {
                                                            //     const newSet = new Set(prev);
                                                            //     newSet.delete(requirement.id as string);
                                                            //     return newSet;
                                                            // });
                                                        }}
                                                        size="small"
                                                        label={requirement.status === 'COMPLETE' ? 'Completed' : 'Mark Complete'}
                                                        labelColor={
                                                            isRequirementRefreshing(requirement.id) || tabState === TAB_STATE.ARCHIVED
                                                                ? '#CBCBCB'
                                                                : requirement.status === 'COMPLETE'
                                                                ? '#00BD00'
                                                                : '#6021B3'
                                                        }
                                                    />
                                                    {requirement.markedAt && requirement.status === 'COMPLETE' ? (
                                                        <div className={styles.ResponseTime}>{formatDate(requirement.markedAt)}</div>
                                                    ) : (
                                                        <div className={styles.ResponseTime}>{formatDate(requirement.createdAt)}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        {searchParams.get(SOURCE.BORROWER_REQ) === 'true' &&
                                            isVisible(VISIBILITY_KEYS.DATA_PENDING.THREE_DOTS) && (
                                                <td style={{ width: '5%' }} className={clsx(styles.TableCell, styles.ThreeDotsCell)}>
                                                    <div
                                                        ref={openMenuDocId === requirement.id ? containerRef : null}
                                                        style={{ position: 'relative', textAlign: 'center' }}
                                                    >
                                                        <div
                                                            style={{ cursor: 'pointer', display: 'inline-block' }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (isRequirementRefreshing(requirement.id)) return;

                                                                setOpenMenuDocId(openMenuDocId === requirement.id ? null : requirement.id);
                                                            }}
                                                        >
                                                            <ThreeDotsIcon
                                                                colour={
                                                                    isRequirementRefreshing(requirement.id)
                                                                        ? '#CBCBCB'
                                                                        : 'var(--primary-text-colour)'
                                                                }
                                                                style={{ cursor: 'pointer' }}
                                                                height="14"
                                                                width="14"
                                                            />
                                                        </div>
                                                        {openMenuDocId === requirement.id && (
                                                            <div className={styles.dropdownMenu}>
                                                                {/* Edit Option - Only show in Active tab and for non-REQ_SOURCE items */}
                                                                {tabState === TAB_STATE.ACTIVE &&
                                                                    !REQ_SOURCE.includes(requirement.source) && (
                                                                        <div
                                                                            className={styles.dropdownItem}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setEditableEntity(requirement);
                                                                                setOpenMenuDocId(null);

                                                                                Mixpanel.track(AICA_DATA_PENDING_EDIT, {
                                                                                    userRole: user?.userData?.accountType,
                                                                                    pnoPlatform: getSubdomain(),
                                                                                    userId: user?.userData?.userId,
                                                                                    currentUrl: window.location.href,
                                                                                    organizationId:
                                                                                        params.id ?? user?.userData?.assesseeOrgId,
                                                                                });
                                                                            }}
                                                                        >
                                                                            Edit
                                                                        </div>
                                                                    )}
                                                                {/* Delete Option - Always available */}
                                                                <div
                                                                    className={styles.dropdownItemDelete}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setShowDeleteModal(requirement.id);
                                                                        setOpenMenuDocId(null);
                                                                    }}
                                                                >
                                                                    Delete
                                                                </div>
                                                                {/* Archive Option - Only show in Active tab */}
                                                                {tabState === TAB_STATE.ACTIVE && (
                                                                    <div
                                                                        className={styles.dropdownItemDelete}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setShowArchiveModal(requirement.id);
                                                                            setOpenMenuDocId(null);
                                                                        }}
                                                                    >
                                                                        Archive
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                        {searchParams.get(SOURCE.LENDER_REQ) === 'true' && (
                                            <td style={{ borderBottom: '1px solid #e0e0e0' }}>
                                                <div
                                                    className={styles.RequestedColumn}
                                                    style={{
                                                        width: '40%',
                                                    }}
                                                >
                                                    {!isEmpty(requirement.lenderRequirementAudit) ? (
                                                        <div className={styles.Requestedtext}>
                                                            <img src={markEmailRead} alt="requested" width={16} height={16} />
                                                        </div>
                                                    ) : (
                                                        <div className={styles.Requestedtext}>
                                                            <span>-</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        )}
                    </table>
                )}
            </div>
            {/* PRIMARY FOOTER - Always visible on Active tab */}
            {!docFetching && shouldShowPrimaryFooter() && (
                <div className={styles.PrimaryFooter}>
                    <div className={styles.FooterContent}>
                        {/* Add Clarification Button */}
                        {isVisible(VISIBILITY_KEYS.DATA_PENDING.ADD_CLARIFICATION) &&
                            (isAddingClarifications ? (
                                <AIText text="Adding clarifications (Please check after 30 seconds)..." showLoadingDots={false} />
                            ) : (
                                <Tooltip
                                    content="Add any data request or clarifications to pending data requests"
                                    direction="bottom"
                                    fitContent={true}
                                    style={{
                                        bottom: '100%',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: '50%',
                                        borderRadius: '12px',
                                    }}
                                    contentCss={{
                                        textAlign: 'center',
                                    }}
                                >
                                    <Button
                                        text={searchParams.get(SOURCE.BORROWER_REQ) === 'true' ? 'Add Clarification' : 'Add Query'}
                                        icon="https://fl-fe-assets.s3.ap-south-1.amazonaws.com/svg/PlusIconPurpleCircle.svg"
                                        iconSize="12px"
                                        onClick={handleOpenAddItemDrawer}
                                        variant="secondary"
                                        style={{ margin: 0, gap: '0.375rem' }}
                                    />
                                </Tooltip>
                            ))}
                    </div>

                    <div className={styles.Dflex}>
                        {/* Last Updated Info */}
                        {lastUpdatedAt && (
                            <span className={styles.FooterText}>
                                {lastUpdatedBy
                                    ? `Last email sent on ${convertEpochToDate(lastUpdatedAt)} by ${lastUpdatedBy}`
                                    : `Last reminder sent ${returnDay(lastUpdatedAt)}`}
                            </span>
                        )}

                        {/* AICA Chase Toggle */}
                        {isAicaChaseEnabled && isVisible(VISIBILITY_KEYS.DATA_PENDING.AICA_CHASE) && (
                            <div className={styles.AicaChaseToggle}>
                                <ToggleButton
                                    checked={aicaChaseData?.status === AicaChaseStatus.ACTIVE}
                                    onChange={handleAicaChaseToggle}
                                    disabled={aicaChaseData?.status === AicaChaseStatus.NOT_APPLICABLE}
                                    variant="aica"
                                />
                                <div className={styles.AicaChaseContent}>
                                    <span className={styles.AicaChaseLabel}>AICA Chase:</span>
                                    <div style={{ fontSize: '0.75rem' }} key={aicaChaseData?.nextDate}>
                                        {renderAicaChaseStatus(
                                            aicaChaseData?.status ?? AicaChaseStatus.NOT_APPLICABLE,
                                            Number(aicaChaseData?.nextDate) ?? null,
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* SELECTION FOOTER - Appears when items are selected */}
            {!docFetching && shouldShowSelectionFooter() && (
                <div
                    className={clsx(styles.SelectionFooter, {
                        [styles.SelectionFooterVisible]: isSelectionFooterVisible,
                    })}
                >
                    <div className={styles.SelectionFooterLeft}>
                        {/* Clarification Button */}
                        <Button
                            text="Add Clarification"
                            icon={plusPurpleCircle}
                            iconSize="16px"
                            onClick={handleOpenAddItemDrawer}
                            variant="secondary"
                            style={{ margin: 0, gap: '0.375rem' }}
                        />
                    </div>

                    <div className={styles.SelectionFooterRight}>
                        <div className={styles.Dflex}>
                            {/* FOR BORROWER SIDE - Update Duration and Archive Buttons */}
                            {searchParams.get(SOURCE.BORROWER_REQ) === 'true' && (
                                <>
                                    {/* Update Duration Button */}
                                    {isPnoUser(user?.userData?.accountType) && (
                                        <Tooltip
                                            content="Update the duration of the selected data request to ask for data till today"
                                            direction="bottom"
                                            fitContent={true}
                                            style={{
                                                bottom: '100%',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                width: '50%',
                                                borderRadius: '12px',
                                            }}
                                            contentCss={{
                                                textAlign: 'center',
                                            }}
                                        >
                                            {(() => {
                                                const isUpdateDurationDisabled =
                                                    isRefreshingRequirements || selectedRequirements.size === 0;
                                                return (
                                                    <Button
                                                        text="Update Duration"
                                                        icon={isUpdateDurationDisabled ? greyCalendar : purpleCalendar}
                                                        textstyle={isUpdateDurationDisabled ? { color: '#CBCBCB' } : undefined}
                                                        iconSize="12px"
                                                        onClick={isUpdateDurationDisabled ? () => {} : handleOpenRefreshRequirementsModal}
                                                        disabled={isUpdateDurationDisabled}
                                                        variant="secondary"
                                                        style={{
                                                            margin: 0,
                                                            gap: '0.375rem',
                                                            ...(isUpdateDurationDisabled ? { backgroundColor: '#ffffff' } : {}),
                                                        }}
                                                    />
                                                );
                                            })()}
                                        </Tooltip>
                                    )}

                                    {/* Archive Button */}
                                    <Tooltip
                                        content="Archive selected data requests"
                                        direction="bottom"
                                        fitContent={true}
                                        style={{
                                            bottom: '100%',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            borderRadius: '12px',
                                        }}
                                        contentCss={{
                                            textAlign: 'center',
                                        }}
                                    >
                                        <Button
                                            text={isArchivingBatch ? 'Archiving...' : 'Archive'}
                                            icon={archive}
                                            iconSize="12px"
                                            onClick={() => {
                                                if (selectedRequirements.size === 1) {
                                                    setShowArchiveModal(Array.from(selectedRequirements)[0]);
                                                } else if (selectedRequirements.size > 1) {
                                                    setShowBulkArchiveModal(true);
                                                }
                                            }}
                                            variant="secondary"
                                            style={{ margin: 0 }}
                                            disabled={selectedRequirements.size === 0 || isArchivingBatch}
                                        />
                                    </Tooltip>
                                </>
                            )}

                            {/* User List Tooltip - BORROWER ONLY */}
                            {searchParams.get(SOURCE.BORROWER_REQ) === 'true' && isVisible(VISIBILITY_KEYS.DATA_PENDING.USER_LIST) && (
                                <Tooltip
                                    content={tooltipContent()}
                                    style={{
                                        bottom: '100%',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: '25rem',
                                        borderRadius: '12px',
                                    }}
                                    direction="bottom"
                                    hideDelay={500}
                                >
                                    <div
                                        style={{
                                            gap: '0.25rem',
                                            cursor: 'pointer',
                                            position: 'relative',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            padding: '0.5rem',
                                            border: '1px solid #E7E7E7',
                                            borderRadius: '0.25rem',
                                            height: '2.75rem',
                                        }}
                                        className={styles.Dflex}
                                    >
                                        <img src={infoIcon} alt="score" height={'16px'} width={'16px'} />
                                        <span className={styles.UserList}>User List</span>
                                    </div>
                                </Tooltip>
                            )}

                            {/* Compose Email Button */}
                            {isVisible(VISIBILITY_KEYS.DATA_PENDING.COMPOSE_EMAIL) && (
                                <Button
                                    style={{
                                        margin: '0',
                                        fontSize: '0.875rem',
                                        padding: '0.75rem 1rem',
                                    }}
                                    text={getComposeEmailButtonText()}
                                    disabled={isComposeEmailDisabled(loading, combinedOrgUserList)}
                                    onClick={() => handleComposeEmailClick(selectedRequirements)}
                                    variant={'primary'}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* Review Email Drawer */}
            <ReviewEmailDrawer
                isOpen={isReviewEmailDrawerOpen}
                onSubmit={handleSubmitReviewEmail}
                onClose={() => setIsReviewEmailDrawerOpen(false)}
                selectedRequirements={selectedRequirementObjects}
            />

            {/* Add Item Drawer */}
            <AddClarificationDrawer
                isOpen={isAddItemDrawerOpen || editableEntity !== null}
                onClose={handleCloseAddItemDrawer}
                onSubmit={handleAddItemSubmit}
                handleEdit={updateRequirementOptimistically}
                isEditing={!isNil(editableEntity)}
                editedEntity={editableEntity}
                organizationId={params.id ?? user?.userData?.assesseeOrgId}
            />
            {showDeleteModal !== null && (
                <ConfigurableModal
                    isOpen={showDeleteModal !== null}
                    deleteFunction={async () => {
                        await updateRequirementOptimistically(
                            showDeleteModal as string,
                            {
                                isDeleted: true,
                            },
                            true,
                        );
                        setShowDeleteModal(null);
                    }}
                    onClose={() => setShowDeleteModal(null)}
                    heading="Delete"
                    message="Are you sure you want to permanently delete selected query?"
                />
            )}

            {/* Single Item Archive Modal */}
            {showArchiveModal !== null && (
                <ConfigurableModal
                    isOpen={showArchiveModal !== null}
                    onClose={() => setShowArchiveModal(null)}
                    heading="Archive"
                    message="Are you sure you want to archive this query? This action cannot be undone."
                    primaryButton={{
                        label: 'Archive',
                        onClick: async () => {
                            // Close modal immediately on button click
                            const itemToArchive = showArchiveModal;
                            setShowArchiveModal(null);
                            setSelectedRequirements(new Set());
                            // Use batch archive for single item too for consistency
                            await handleArchiveBatch([itemToArchive as string]);
                        },
                    }}
                />
            )}

            {/* Bulk Archive Modal */}
            {showBulkArchiveModal && (
                <ConfigurableModal
                    isOpen={showBulkArchiveModal}
                    onClose={() => setShowBulkArchiveModal(false)}
                    heading="Archive Multiple Queries"
                    message={`Are you sure you want to archive ${selectedRequirements.size} queries? This action cannot be undone.`}
                    primaryButton={{
                        label: `Archive ${selectedRequirements.size} Queries`,
                        onClick: async () => {
                            setShowBulkArchiveModal(false);
                            await handleArchiveSelected();
                        },
                    }}
                />
            )}

            {isRefreshRequirementsModalOpen && (
                <ConfigurableModal
                    isOpen={isRefreshRequirementsModalOpen}
                    deleteFunction={async () => {
                        await handleRefreshRequirements(selectedRequirements, isAllSelected);
                    }}
                    onClose={() => {
                        handleUpdateDurationModalClose(isAllSelected, selectedRequirements);
                    }}
                    heading="Update Duration"
                    message="The duration of the selected data requests will be updated as of today. Data requests without a defined duration will not be updated.
                         Do you wish to proceed?"
                    primaryButton={{
                        label: 'Proceed',
                        style: { backgroundColor: '#6021B3' },
                        onClick: async () => {
                            await handleRefreshRequirements(selectedRequirements, isAllSelected);
                        },
                    }}
                />
            )}
        </>
    );
};

export default NeedInfoV2;
